//@flow
import {ActionsObservable, ofType} from 'redux-observable';
import {debounceTime, map, switchMap} from 'rxjs/operators';

import {ActionCreator, createReducer, buildCatchError} from '../../store/utils';
import {buildRoute} from '../../http';

import {
  validateSslApi,
  generateSslApi,
  getDomainsApi,
  endpoints,
  deleteCustomDomainApi,
} from './api';

import type {Domain} from '../../entities';

export type CustomDomainAction = {
  sslIsValid?: boolean,
  error?: string,
  customDomain?: Domain,
};

export type CustomDomainState = {
  isValidatingSsl?: boolean,
  sslIsValid?: boolean,
  startGeneratingSsl?: boolean,
  isGeneratingSsl: boolean,
  errorGenerating?: string,
  errorValidating?: string,
  customDomain?: Domain,
  domainDeleted?: boolean,
};

export class ValidateSsl extends ActionCreator<CustomDomainAction> {}
export class ValidateSslSuccess extends ActionCreator<CustomDomainAction> {}
export class ValidateSslErrorFn extends ActionCreator<CustomDomainAction> {}

export class GenerateSsl extends ActionCreator<CustomDomainAction> {}
export class GenerateSslSuccess extends ActionCreator<CustomDomainAction> {}
export class GenerateSslErrorFn extends ActionCreator<CustomDomainAction> {}

export class GetCustomDomain extends ActionCreator<CustomDomainAction> {}
export class GetCustomDomainSuccess extends ActionCreator<CustomDomainAction> {}
export class GetCustomDomainErrorFn extends ActionCreator<CustomDomainAction> {}

export class DeleteCustomDomain extends ActionCreator<CustomDomainAction> {}
export class DeleteCustomDomainSuccess extends ActionCreator<CustomDomainAction> {}
export class DeleteCustomDomainErrorFn extends ActionCreator<CustomDomainAction> {}

export const actions = {
  validateSsl: (payload: CustomDomainAction) => new ValidateSsl(payload),
  generateSsl: (payload: CustomDomainAction) => new GenerateSsl(payload),
  getCustomDomain: (payload: CustomDomainAction) =>
    new GetCustomDomain(payload),
  deleteCustomDomain: (payload: CustomDomainAction) =>
    new DeleteCustomDomain(payload),
};

export const initialState = {
  isValidatingSsl: false,
  sslIsValid: undefined,
  isGeneratingSsl: false,
  customDomain: undefined,
};

export default createReducer<CustomDomainState>(initialState, {
  [ValidateSsl.type]: (state: CustomDomainState) => ({
    ...state,
    isValidatingSsl: true,
    sslIsValid: undefined,
  }),
  [ValidateSslSuccess.type]: (
    state: CustomDomainState,
    action: CustomDomainAction
  ) => ({
    ...state,
    errorValidating: undefined,
    isValidatingSsl: false,
    sslIsValid: action.sslIsValid,
  }),
  [ValidateSslErrorFn.type]: (state: CustomDomainState) => ({
    ...state,
    isValidatingSsl: false,
    sslIsValid: false,
    errorValidating: 'Dominio no encontrado',
  }),

  [GenerateSsl.type]: state => ({...state, startGeneratingSsl: true}),
  [GenerateSslSuccess.type]: state => ({
    ...state,
    startGeneratingSsl: false,
    isGeneratingSsl: true,
  }),
  [GenerateSslErrorFn.type]: state => ({
    ...state,
    isGeneratingSsl: false,
    startGeneratingSsl: false,
    errorGenerating: 'Error generando certificado',
  }),
  [GetCustomDomain.type]: state => ({...state, customDomain: undefined}),
  [GetCustomDomainSuccess.type]: (
    state: CustomDomainState,
    action: CustomDomainAction
  ) => ({
    ...state,
    customDomain: action.customDomain,
  }),
  [GetCustomDomainErrorFn.type]: state => ({
    ...state,
    customDomain: undefined,
  }),
  [DeleteCustomDomainSuccess.type]: state => {
    return {...state, domainDeleted: true};
  },
});

export function validateSslEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(ValidateSsl.type),
    debounceTime(500),
    switchMap(({domain}) => {
      return validateSslApi({url: domain}).pipe(
        map((isAvailableToGenerateSsl: boolean) => {
          return new ValidateSslSuccess({sslIsValid: isAvailableToGenerateSsl});
        }),
        buildCatchError(ValidateSslErrorFn)
      );
    })
  );
}

export function generateSslEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(GenerateSsl.type),
    debounceTime(500),
    switchMap(({domain}) => {
      return generateSslApi({domainName: domain}).pipe(
        map(() => new GenerateSslSuccess()),
        buildCatchError(GenerateSslErrorFn)
      );
    })
  );
}

export function getCustomDomainEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(GetCustomDomain.type),
    debounceTime(500),
    switchMap(({storeId}) => {
      return getDomainsApi(undefined, {
        route: buildRoute(endpoints.GET_DOMAINS, [storeId]),
      }).pipe(
        map((domains: Domain[]) => {
          for (const domain of domains) {
            const {domainName} = domain;
            const hasCustomDomain = !domainName.includes('tiendada');
            if (hasCustomDomain) {
              return new GetCustomDomainSuccess({customDomain: domain});
            }
          }
          return new GetCustomDomainSuccess();
        }),
        buildCatchError(GetCustomDomainErrorFn)
      );
    })
  );
}

export function deleteCustomDomainEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(DeleteCustomDomain.type),
    debounceTime(500),
    switchMap(({storeId}) => {
      return deleteCustomDomainApi().pipe(
        map(() => new DeleteCustomDomainSuccess()),
        buildCatchError(DeleteCustomDomainErrorFn)
      );
    })
  );
}
